<template>
  <MolgenisAny
    :modelValue="session"
    :menu-items="menuItems"
    @update:modelValue="onSessionUpdate"
    style="background-color: white"
  >
    <template #banner>
      <div v-html="banner" />
    </template>
    <Error />
    <output v-if="showSessionDebug" class="session-debug-line" aria-live="polite">
      <strong>Session roles:</strong> {{ sessionRolesLabel }}
    </output>
    <GTag
      v-if="settingsStore.googleAnalyticsId"
      :tagId="settingsStore.googleAnalyticsId"
    />
    <Teleport v-if="deploymentSlot" :to="deploymentSlot">
      <DeploymentBadge :label="deploymentLabel" />
    </Teleport>
    <Teleport v-if="callLinksSlot" :to="callLinksSlot">
      <NavbarCallLinks :links="callLinks" />
    </Teleport>
    <RouterView @click="closeAllDropdownButtons" />
    <template #footer>
      <div v-html="footer" />
      <Footer id="directory-footer" :session="session" />
    </template>
  </MolgenisAny>
</template>

<script setup>
import { useFavicon } from "@vueuse/core";
//@ts-expect-error
import { Molgenis, GTag } from "molgenis-components";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import DeploymentBadge from "./components/DeploymentBadge.vue";
import NavbarCallLinks from "./components/NavbarCallLinks.vue";
import Error from "./components/Error.vue";
import Footer from "./components/Footer.vue";
import { applyBookmark } from "./functions/bookmarkMapper.js";
import { buildCallLinks } from "./functions/navbarCallLinks.js";
import { isSignedIn } from "./functions/session.js";
import router from "./router/index.js";
import { useFiltersStore } from "./stores/filtersStore.js";
import { useSettingsStore } from "./stores/settingsStore.js";

const MolgenisAny = Molgenis;

/* Paired with the rule of the same name in styles/app-overrides.css. */
const HIDE_NAVBAR_SESSION_CLASS = "hide-navbar-session";

const route = useRoute();
const query = computed(() => route.query);

const filtersStore = useFiltersStore();
const settingsStore = useSettingsStore();

const { configurationFetched } = storeToRefs(settingsStore);

const banner = computed(() => settingsStore.config.banner);
const footer = computed(() => settingsStore.config.footer);
const logoURL = computed(() => settingsStore.config.logoURL);
const faviconURL = computed(() => settingsStore.config.faviconURL);
const deploymentLabel = computed(() => settingsStore.deploymentLabel);
/* Help is the only item the vendored menu renders: it rewrites every href to
 * `/<schema>/<href>`, so only in-schema links survive it. The call links are
 * absolute URLs and are teleported in instead - see ensureCallLinksSlot(). */
const menuItems = computed(() => [
  {
    label: "Help",
    href: settingsStore.config.helpUrl || "docs",
    submenu: [],
  },
]);
const callLinks = computed(() => buildCallLinks(settingsStore.config));

const session = ref({});
const deploymentSlot = ref(null);
const callLinksSlot = ref(null);
let navbarLogoObserver = null;

const showSessionDebug = computed(() => import.meta.env.DEV);
const signedIn = computed(() => isSignedIn(session.value));
const sessionRolesLabel = computed(() => {
  const roles = session.value?.roles;
  if (!Array.isArray(roles) || roles.length === 0) {
    return "none";
  }

  return roles.join(", ");
});

window.onpopstate = function () {
  filtersStore.bookmarkWaitingForApplication = true;
  applyBookmark(query.value);
};

function onSessionUpdate(nextSession) {
  session.value = nextSession;
  settingsStore.setSessionInformation(nextSession);
}

watch(configurationFetched, () => {
  applyNavbarBrandLogoOverride();
  changeFavicon();
  initMatomo();
});

watch(deploymentLabel, () => {
  ensureDeploymentSlot();
});

watch(callLinks, () => {
  ensureCallLinksSlot();
});

watch(logoURL, () => {
  applyNavbarBrandLogoOverride();
});

watch(faviconURL, () => {
  changeFavicon();
});

/**
 * Hide the vendored navbar's Sign up / Sign in buttons from catalogue visitors
 * (issue #28): there is nothing for them to sign into, so the buttons only
 * mislead. Administrators sign in through the #/signin route instead.
 *
 * The buttons come from the vendored MolgenisSession component, which takes no
 * prop or slot to switch them off and is also the only thing that fetches
 * `_session`, `_settings` and `_manifest` - unmounting it would take the
 * Manager gate, the footer version line and the vendor CSS settings with it.
 * So the component keeps running and app-overrides.css hides its controls
 * while nobody is signed in. Once an administrator signs in the same area
 * carries "Hi <email>" and Sign out, so it is shown again.
 */
watch(
  signedIn,
  (hasSession) => {
    document.body.classList.toggle(HIDE_NAVBAR_SESSION_CLASS, !hasSession);
  },
  { immediate: true }
);

onMounted(async () => {
  filtersStore.bookmarkWaitingForApplication = true;

  /* Navbar work runs before the awaits below: router.isReady() rejects when the
   * configuration fetch fails, and the deployment marker (which can come from
   * VITE_DEPLOYMENT alone) has to show up on a broken backend too. */
  observeNavbarBrandLogo();
  applyNavbarBrandLogoOverride();
  ensureDeploymentSlot();
  ensureCallLinksSlot();
  changeFavicon();

  await router.isReady();
  applyBookmark(query.value);
});

onBeforeUnmount(() => {
  if (navbarLogoObserver) {
    navbarLogoObserver.disconnect();
    navbarLogoObserver = null;
  }
  document.body.classList.remove(HIDE_NAVBAR_SESSION_CLASS);
});

function closeAllDropdownButtons(event) {
  const allDropdownButtons = document.querySelectorAll(".dropdown-button");
  if (event.target?.id) {
    allDropdownButtons.forEach((dropdownButton) => {
      if (dropdownButton.id !== event.target?.id) {
        dropdownButton.removeAttribute("open");
      }
    });
  } else {
    allDropdownButtons.forEach((dropdownButton) => {
      dropdownButton.removeAttribute("open");
    });
  }
}

function applyNavbarBrandLogoOverride() {
  if (!logoURL.value) return;

  const logoElements = document.querySelectorAll('img[alt="brand-logo"]');
  logoElements.forEach((element) => {
    if (element.getAttribute("src") !== logoURL.value) {
      element.setAttribute("src", logoURL.value);
    }
  });
}

function observeNavbarBrandLogo() {
  if (navbarLogoObserver) return;

  navbarLogoObserver = new MutationObserver(() => {
    applyNavbarBrandLogoOverride();
    ensureDeploymentSlot();
    ensureCallLinksSlot();
  });

  navbarLogoObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/**
 * The navbar is rendered by the vendored Molgenis component, which exposes no
 * slot for extra items, so the deployment badge is teleported into an anchor we
 * place in nav.navbar ourselves - same approach as the brand logo override.
 *
 * The anchor goes *before* the navbar's last child (the sign-in area) because
 * app-overrides.css styles that area via `.navbar > div:last-child`. Every step
 * is idempotent: this runs from the MutationObserver above, which our own DOM
 * changes trigger, so it must do nothing once the anchor sits in place.
 */
function ensureDeploymentSlot() {
  const navbar = document.querySelector("nav.navbar");
  if (!navbar) return;

  let slot = navbar.querySelector(":scope > .app-deployment-slot");

  if (!deploymentLabel.value) {
    if (slot) {
      /* Drop the ref first so the Teleport unmounts before its target does. */
      const staleSlot = slot;
      deploymentSlot.value = null;
      nextTick(() => staleSlot.remove());
    }
    return;
  }

  if (!slot) {
    slot = document.createElement("div");
    slot.className = "app-deployment-slot";
  }

  const sessionElement = navbar.lastElementChild;
  if (sessionElement !== slot && slot.nextElementSibling !== sessionElement) {
    navbar.insertBefore(slot, sessionElement);
  }

  deploymentSlot.value = slot;
}

/**
 * Anchor for the call links (NavbarCallLinks.vue), placed in ul.navbar-nav
 * ahead of Help.
 *
 * They cannot go through the `menuItems` prop: the vendored Molgenis component
 * rewrites every menu href to `/<schema>/<href>` whenever the app is served
 * under a schema path, which turns an absolute URL into
 * `/<schema>/https://isidore-project.eu/...`. Patching the rendered href would
 * not help either - the menu's click handler navigates to the rewritten value
 * it kept in memory, not to the attribute.
 *
 * Same teleport approach, and the same idempotence requirement, as
 * ensureDeploymentSlot() above: this runs from the MutationObserver that our
 * own DOM changes trigger.
 */
function ensureCallLinksSlot() {
  const navList = document.querySelector("nav.navbar ul.navbar-nav");
  if (!navList) return;

  let slot = navList.querySelector(":scope > .app-call-links-slot");

  if (!callLinks.value.length) {
    if (slot) {
      /* Drop the ref first so the Teleport unmounts before its target does. */
      const staleSlot = slot;
      callLinksSlot.value = null;
      nextTick(() => staleSlot.remove());
    }
    return;
  }

  if (!slot) {
    slot = document.createElement("li");
    slot.className = "nav-item app-call-links-slot";
  }

  if (navList.firstElementChild !== slot) {
    navList.insertBefore(slot, navList.firstElementChild);
  }

  callLinksSlot.value = slot;
}

function changeFavicon() {
  const faviconUrl = getFaviconUrl();
  useFavicon(faviconUrl);
}

function getFaviconUrl() {
  return faviconURL.value || "/isidore-favicon.png";
}

function initMatomo() {
  const { matomoUrl, matomoSiteId } = settingsStore.config;

  if (matomoUrl && matomoSiteId) {
    const _paq = (window._paq = window._paq || []);
    /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
    _paq.push(["trackPageView"]);
    _paq.push(["enableLinkTracking"]);
    (function () {
      _paq.push(["setTrackerUrl", matomoUrl + "matomo.php"]);
      _paq.push(["setSiteId", matomoSiteId]);
      const doc = document;
      const newScriptElement = doc.createElement("script");
      const firstFoundScriptElement = doc.getElementsByTagName("script")[0];
      newScriptElement.async = true;
      newScriptElement.src = matomoUrl + "matomo.js";
      firstFoundScriptElement.parentNode?.insertBefore(
        newScriptElement,
        firstFoundScriptElement
      );
    })();

    router.afterEach(() => {
      if (window._paq) {
        window._paq.push(["setCustomUrl", window.location.href]);
        window._paq.push(["setDocumentTitle", document.title]);
        window._paq.push(["trackPageView"]);
      }
    });
  } else {
    console.warn(
      `Matomo URL (${matomoUrl}) or Site ID (${matomoSiteId}) is not set in the configuration.`
    );
  }
}
</script>

<style scoped>
.session-debug-line {
  position: fixed;
  right: 0.75rem;
  bottom: 0.75rem;
  z-index: 2000;
  background: rgba(17, 24, 39, 0.92);
  color: #fff;
  padding: 0.4rem 0.6rem;
  border-radius: 0.4rem;
  font-size: 0.8rem;
  line-height: 1.2;
}
</style>
